import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTableModule } from '@angular/material/table';
import { catchError, finalize, map, of, shareReplay, startWith, switchMap } from 'rxjs';
import { AdminCitiesApi } from '../../../core/api/cities/admin-cities.api';
import { CityItem } from '../../../core/api/cities/admin-cities.models';
import { ListingLayoutComponent } from '../../../shared/layouts/listing-layout/listing-layout.component';
import { HeadingComponent } from '../../../shared/page/heading/heading.component';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';

interface CitiesVm {
  isLoading: boolean;
  error: string;
  items: CityItem[];
}

@Component({
  selector: 'app-all-cities',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ListingLayoutComponent,
    HeadingComponent,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatTableModule,
  ],
  templateUrl: './all-cities.component.html',
  styleUrl: './all-cities.component.scss',
})
export class AllCitiesComponent {
  private readonly adminCitiesApi = inject(AdminCitiesApi);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly fb = inject(FormBuilder);

  readonly displayedColumns = ['cityName', 'state', 'createdAt', 'updatedAt', 'actions'];
  readonly reloadTick = signal(0);
  readonly isSubmitting = signal(false);
  readonly submitSuccess = signal('');
  readonly submitError = signal('');
  readonly deletingCityIds = signal<Set<string>>(new Set<string>());

  readonly form = this.fb.group({
    cityName: ['', [Validators.required]],
    state: ['', [Validators.required]],
  });

  readonly citiesVm$ = toObservable(this.reloadTick).pipe(
    switchMap(() =>
      this.adminCitiesApi.getCities().pipe(
        map((response): CitiesVm => ({
          isLoading: false,
          error: response.success ? '' : response.message || 'Unable to fetch cities.',
          items: response.success ? response.data ?? [] : [],
        })),
        catchError((error) =>
          of({
            isLoading: false,
            error: error?.error?.message || 'Something went wrong while fetching cities.',
            items: [],
          } satisfies CitiesVm),
        ),
        startWith({
          isLoading: true,
          error: '',
          items: [],
        } satisfies CitiesVm),
      ),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  submit(): void {
    this.submitSuccess.set('');
    this.submitError.set('');

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSubmitting.set(true);

    this.adminCitiesApi
      .createCity({
        cityName: String(this.form.get('cityName')?.value ?? '').trim(),
        state: String(this.form.get('state')?.value ?? '').trim(),
      })
      .pipe(finalize(() => this.isSubmitting.set(false)))
      .subscribe({
        next: (response) => {
          if (!response.success) {
            this.submitError.set(response.message || 'Unable to create city.');
            return;
          }

          this.submitSuccess.set(response.message || 'City created successfully.');
          this.form.reset();
          this.reloadTick.update((value) => value + 1);
        },
        error: (error) => {
          this.submitError.set(error?.error?.message || 'Something went wrong while creating city.');
        },
      });
  }

  deleteCity(city: CityItem): void {
    if (!city?.id || this.isDeleting(city.id)) {
      return;
    }

    this.confirmDialog
      .open({
        title: 'Delete City',
        message: `Do you want to delete ${city.cityName}?`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
      })
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }

        this.submitSuccess.set('');
        this.submitError.set('');
        this.setDeleting(city.id, true);

        this.adminCitiesApi
          .deleteCity(city.id)
          .pipe(finalize(() => this.setDeleting(city.id, false)))
          .subscribe({
            next: (response) => {
              if (!response.success) {
                this.submitError.set(response.message || 'Unable to delete city.');
                return;
              }

              this.submitSuccess.set(response.message || 'City deleted successfully.');
              this.reloadTick.update((value) => value + 1);
            },
            error: (error) => {
              this.submitError.set(error?.error?.message || 'Something went wrong while deleting city.');
            },
          });
      });
  }

  isDeleting(id: string): boolean {
    return this.deletingCityIds().has(id);
  }

  formatDate(date?: string | null): string {
    if (!date) {
      return 'NA';
    }

    return new Intl.DateTimeFormat('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(date));
  }

  private setDeleting(id: string, isDeleting: boolean): void {
    const next = new Set(this.deletingCityIds());
    if (isDeleting) {
      next.add(id);
    } else {
      next.delete(id);
    }
    this.deletingCityIds.set(next);
  }
}
